<script setup>
/**
 * LLM token_tiered 价目编辑器。界面输入人民币元/百万 tokens；末档输入上限留空表示不封顶。
 */
import { computed } from 'vue'
import { Plus, Trash2 } from '@lucide/vue'
import { Button, FormField, Input } from '@drama/ui'
import { useI18n } from 'vue-i18n'

/** @type {import('vue').ModelRef<import('../llm-model-form.js').ModelForm>} */
const form = defineModel({ type: Object, required: true })

const props = defineProps({
  /**
   * `validatePricingForm` 的产物是 `{ rows: { 档位下标: { 字段: 错误码 } }, rule: 错误码|null }`。
   * @type {import('vue').PropType<Record<string, any>>}
   */
  errors: { type: Object, default: () => ({ rows: [], rule: null }) },
  /** 提交过一次之后才显示错误 —— 刚打开就满屏红字是在骂人。 */
  showErrors: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
})

const { t } = useI18n()

/** @param {number} index @param {string} field */
function tierErrorOf(index, field) {
  if (!props.showErrors) return ''
  const code = props.errors.rows[index][field]
  return code ? t(`visualGameAdmin.llm.models.errors.${code}`) : ''
}

/** 三条硬约束里被违反的那一条,指名道姓 —— 不是笼统一句「价目非法」。 */
const tiersRuleError = computed(() => {
  if (!props.showErrors || props.errors.rule === null) return ''
  return t(`visualGameAdmin.llm.models.errors.tiers.${props.errors.rule}`)
})

function addTier() {
  // 新的一档插在末档之前:末档是那个不封顶的兜底档,它必须一直待在最后。
  const tiers = form.value.tiers
  tiers.splice(Math.max(tiers.length - 1, 0), 0, {
    input_up_to: '',
    input_yuan_per_m: '',
    output_yuan_per_m: '',
    cached_input_yuan_per_m: '',
    cache_write_input_yuan_per_m: '',
  })
}

/** @param {number} index */
function removeTier(index) {
  form.value.tiers.splice(index, 1)
}
</script>

<template>
  <section
    class="pricing"
    data-insp-path="apps/visual-game-admin/src/pages/llm/provider/PricingEditor.vue#pricing-editor"
  >
    <div
      class="pricing__tiers"
    >
      <header class="pricing__tiers-header">
        <h3>{{ t('visualGameAdmin.llm.models.pricing.tiers.title') }}</h3>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          :disabled="props.disabled"
          @click="addTier"
        >
          <Plus :size="16" />
          {{ t('visualGameAdmin.llm.models.pricing.tiers.add') }}
        </Button>
      </header>

      <p
        v-if="tiersRuleError"
        class="vga-error pricing__rule-error"
        role="alert"
      >
        {{ tiersRuleError }}
      </p>

      <div
        v-for="(tier, index) in form.tiers"
        :key="index"
        class="pricing__tier"
      >
        <header class="pricing__tier-header">
          <strong>{{ t('visualGameAdmin.llm.models.pricing.tiers.tier', { index: index + 1 }) }}</strong>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            :disabled="props.disabled || form.tiers.length === 1"
            @click="removeTier(index)"
          >
            <Trash2 :size="16" />
            {{ t('visualGameAdmin.llm.models.pricing.tiers.remove') }}
          </Button>
        </header>
        <div class="pricing__tier-fields">
          <FormField
            :label="t('visualGameAdmin.llm.models.pricing.tiers.upTo')"
            :error="tierErrorOf(index, 'input_up_to')"
          >
            <Input
              v-model="tier.input_up_to"
              type="number"
              min="1"
              step="1"
              :disabled="props.disabled"
              :placeholder="t('visualGameAdmin.llm.models.pricing.tiers.unbounded')"
              :invalid="Boolean(tierErrorOf(index, 'input_up_to'))"
            />
          </FormField>
          <FormField
            :label="t('visualGameAdmin.llm.models.pricing.tiers.inputRate')"
            :error="tierErrorOf(index, 'input_yuan_per_m')"
            required
          >
            <Input
              v-model="tier.input_yuan_per_m"
              type="number"
              min="0"
              step="0.000001"
              :disabled="props.disabled"
              :invalid="Boolean(tierErrorOf(index, 'input_yuan_per_m'))"
            />
          </FormField>
          <FormField
            :label="t('visualGameAdmin.llm.models.pricing.tiers.outputRate')"
            :error="tierErrorOf(index, 'output_yuan_per_m')"
            required
          >
            <Input
              v-model="tier.output_yuan_per_m"
              type="number"
              min="0"
              step="0.000001"
              :disabled="props.disabled"
              :invalid="Boolean(tierErrorOf(index, 'output_yuan_per_m'))"
            />
          </FormField>
          <FormField
            :label="t('visualGameAdmin.llm.models.pricing.tiers.cachedRate')"
            :error="tierErrorOf(index, 'cached_input_yuan_per_m')"
            required
          >
            <Input
              v-model="tier.cached_input_yuan_per_m"
              type="number"
              min="0"
              step="0.000001"
              :disabled="props.disabled"
              :invalid="Boolean(tierErrorOf(index, 'cached_input_yuan_per_m'))"
            />
          </FormField>
          <FormField
            :label="t('visualGameAdmin.llm.models.pricing.tiers.cacheWriteRate')"
            :error="tierErrorOf(index, 'cache_write_input_yuan_per_m')"
            required
          >
            <Input
              v-model="tier.cache_write_input_yuan_per_m"
              type="number"
              min="0"
              step="0.000001"
              :disabled="props.disabled"
              :invalid="Boolean(tierErrorOf(index, 'cache_write_input_yuan_per_m'))"
            />
          </FormField>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.pricing {
  display: grid;
  gap: var(--drama-space-md);
  padding: var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);

  &__tiers {
    display: grid;
    gap: var(--drama-space-md);
  }

  &__tiers-header,
  &__tier-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--drama-space-md);
  }

  &__tiers-header h3 {
    margin: 0;
    font-size: var(--drama-font-size-sm);
  }

  &__rule-error {
    margin: 0;
    padding: var(--drama-space-md);
    border: 1px solid var(--drama-color-danger);
    border-radius: var(--drama-radius-sm);
    background: color-mix(in srgb, var(--drama-color-danger) 10%, transparent);
    line-height: var(--drama-line-height-normal);
  }

  &__tier {
    display: grid;
    gap: var(--drama-space-sm);
    padding: var(--drama-space-md);
    border: 1px solid var(--drama-color-border);
    border-radius: var(--drama-radius-sm);
    background: var(--drama-color-surface);
  }

  &__tier-header strong {
    font-size: var(--drama-font-size-sm);
  }

  &__tier-fields {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: start;
    gap: var(--drama-space-md);
  }
}

@media (max-width: 900px) {
  .pricing__tier-fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .pricing__tier-fields {
    grid-template-columns: 1fr;
  }
}
</style>
